
<template>
    <div class="card">
        <DataTable lazy dataKey="uuid"
            stateStorage="local"
            ref="dt"
            :state-key="localStorageKey"
            :value="tableData"
            :paginator="true"
            :rows="rows"
            :rowHover="true"
            sortMode="single"
            removableSort
            :metaKeySelection="false"
            :loading="isLoading"
            :totalRecords="totalRecords"
            :currentPage="currentPage"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            :rowsPerPageOptions="rowsPerPageOptions"
            currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
            @page="onPaginate($event)"
            @sort="onSort($event)">
            <template #header>
                <div class="flex justify-end" v-if="firstBackOfficeUserSupplier">
                    <Button
                        size="small"
                        title="Activer/désactiver les utilisateurs"
                        class="btn p-button-success"
                        style="margin-right: 0.5em"
                        @click="toggleBackOfficeStatusAndLinkedUsers(firstBackOfficeUserSupplier, true)">
                        Activer les utilisateurs
                    </Button>
                    <Button
                        size="small"
                        title="Activer/désactiver les utilisateurs"
                        class="btn p-button-danger"
                        @click="toggleBackOfficeStatusAndLinkedUsers(firstBackOfficeUserSupplier, false)">
                        Désactiver les utilisateurs
                    </Button>
                </div>
                <hr class="m-5" />
                <div class="flex flex-row flex-nowrap card-header border-0">
                    <div class="flex card-toolbar w-full">
                        <div class="w-full">
                            <span class="p-input-icon-left w-10/12">
                                <i class="pi pi-search" />
                                <InputText type="text" class="w-9/12" v-model="criteria" placeholder="Recherche par email, nom, prénom" 
                                    @input="onSearchInputDt()" />
                            </span>
                        </div>
                    </div>
                    <div class="card-toolbar w-full">
                        <div class="flex flex-col justify-center">
                        </div>
                    </div>
                    <div class="card-toolbar w-full">
                        <div class="flex justify-end" v-if="supplierUuidFromUrl.length">
                            <Button size="small" 
                                class="btn btn-primary p-2 ps-2 pe-2"
                                style="margin-right: 0.5em" 
                                title="Ajouter un forunisseur"
                                @click="addNewBackOfficeUser()">
                                Ajouter un utilisateur
                            </Button>
                        </div>
                    </div>
                </div>
            </template>
            <div class="card-body pt-0">
                <Column field="user.lastname" header="Nom">
                    <template #body="slotProps">
                        <span> {{ slotProps.data.user.lastname }}</span>
                    </template>
                </Column>
                <Column field="user.firstname" header="Prénom">
                    <template #body="slotProps">
                        <span> {{ slotProps.data.user.firstname }}</span>
                    </template>
                </Column>
                <Column field="user.email" header="E-mail">
                    <template #body="slotProps">
                        <span> {{ slotProps.data.user.email }} </span>
                    </template>
                </Column>
                <Column field="user.enabled" header="État" sortable>
                    <template #body="slotProps">
                        <div v-if="slotProps.data.isActive"
                            class="text-center bg-green-100 text-green-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full dark:bg-green-900 dark:text-green-300">
                            Actif
                        </div>
                        <div v-else 
                            class="text-center bg-red-100 text-red-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300">
                            Inactif
                        </div>
                    </template>
                </Column>
                <Column field="createdAt" header="Date d'ajout" sortable>
                    <template #body="slotProps">
                        <span> {{ slotProps.data.createdAt }} </span>
                    </template>
                </Column>
                <Column field="createdBy" header="Créé par">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.createdBy !== undefined">
                            {{ slotProps.data.createdBy.firstname }} {{ slotProps.data.createdBy.lastname }}
                        </span>
                        <span v-else>
                            {{ "" }}
                        </span>
                    </template>
                </Column>
                <Column headerStyle="width: 12em;">
                    <template #header>
                        <div>Actions</div>
                    </template>
                    <template #body="slotProps">
                        <InputSwitch style="margin-right: 0.5em"
                            v-model="slotProps.data.isActive"
                            :input-id="slotProps.data.user.uuid" 
                            @input="toggleUserStatus(
                                slotProps.data.user.uuid,
                                slotProps.data.isActive,
                                slotProps.data.isActive
                                    ? `Êtes-vous sûr de vouloir activer cet utilisateur ?`
                                    : `Êtes-vous sûr de vouloir désactiver cet utilisateur ?`,
                                slotProps.data.isActive ? `Utilisateur activé` : `Utilisateur désactivé`,
                                `Annuler`,
                                slotProps.data.isActive ? `Oui, activer` : `Oui, désactiver`
                            )"
                        />
                        <Button size="small" icon="pi pi-trash" class="p-button-danger" 
                            :class="`${slotProps.data.uuid}`"
                            style="margin-right: 0.5em" title="supprimer"
                            @click="deleteBackOfficeSupplierUser(slotProps.data.uuid)"
                        >
                        </Button>
                      <!--<Button size="small" icon="pi pi-pencil" class="p-button-warning"
                          title="Éditer l'utilisateur"
                          style="margin-right: 0.5em"
                          @click="redirectToEditUserPage(slotProps.data.uuid)">
                      </Button>-->
                    </template>
                </Column>
            </div>
            <template #empty>Aucun résultat trouvé.</template>
        </DataTable>
        <!-- begin::add back office user form modal -->
        <BackOfficeUserCreateFormModal 
            :supplier-uuid="supplierUuidFromUrl"
            :add-back-office-user-form-modalid="addBackOfficeUserFormModalid" />
        <!-- end:: add back office user form modal -->
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from "vue";
import ApiService from "@/_services/api.service";
import swal from "@/_helpers/swal";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputSwitch from "primevue/inputswitch";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import { useRoute } from "vue-router";
import { getPaginateBackOfficeUsersBySupplier } from "@/_services/supplier/backOfficeSupplier/backofficeSupplier";
import { ISortType } from "@/models/backOfficeSupplier/ISortType";
import { IBackOfficeSupplierUser } from "@/models/backOfficeSupplier/IBackOfficeSupplierUser";

const addBackOfficeUserFormModalid = "addBackOfficeUserFormModal";
const localStorageKey = "supplierBackOfficeUser";
const isLoading = ref<boolean>(true);
const backOfficeUsersSuppliers: IBackOfficeSupplierUser[] = [];
const tableData = ref<Array<IBackOfficeSupplierUser>>(backOfficeUsersSuppliers);
const firstBackOfficeUserSupplier = ref<IBackOfficeSupplierUser | undefined>(undefined);
const currentPage = ref<number>(0);
const rows = ref<number>(10);
const totalRecords = ref<number>(0);
const page = ref<number>(1);
const criteria = ref<string>("");
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100];
const defaultsSort = {
    fieldLabel: "order[createdAt]",
    fieldValue: "desc",
};
const route = useRoute();
const supplierUuidFromUrl: string = route.params.uuid ? route.params.uuid.toString() : "";
import BackOfficeUserCreateFormModal from "@/components/modal/backOfficeUser/backOfficeUserCreateFormModal.vue";
import Swal from "@/_helpers/swal";
import {filterService} from "@/_services/filter/category/filter";

onMounted(async () => {
    if (supplierUuidFromUrl.length) {
        await populateDataTableWithLocalStorageFilters(supplierUuidFromUrl);
    }
});

function onSearchInputDt() {
    if (supplierUuidFromUrl.length) {
        populateDatatable(supplierUuidFromUrl, rows.value, page.value, criteria.value);
    }
}

async function populateDatatable(
    supplierUuidFromUrl: string,
    itemsPerPage: number,
    page: number,
    criteria?: string,
    sort?: ISortType,
) {
    showSpinner();
    const [loadingDt, totalRecordsDt, tableDataDt] = await getPaginateBackOfficeUsersBySupplier(
        supplierUuidFromUrl,
        itemsPerPage,
        page,
        criteria,
        sort,
    );
    isLoading.value = loadingDt.value;
    totalRecords.value = totalRecordsDt.value;
    tableData.value = tableDataDt.value;

    firstBackOfficeUserSupplier.value = tableData.value[0];
}

async function populateDataTableWithLocalStorageFilters(
    supplierUuidFromUrl,
    itemsPerPage?: number,
    page?: number
) {
    if (supplierUuidFromUrl.length) {
        let sort: ISortType | undefined  = undefined;
        let localStorageData = getLocalStorageData();
        if (null !== localStorageData) {
            if (localStorageData?.sortField && localStorageData?.sortOrder) {
                sort = {
                    fieldLabel: "order[" + localStorageData.sortField + "]",
                    fieldValue: localStorageData.sortOrder === 1 ? "asc" : "desc"
                };
            }
        }

        let localStorageRowPerPage = getLocalStorageRowsPerPage();
        let localStorageFisrtPageState = getLocalStorageFisrtPageState();
        let currentPage = page;
        if (!currentPage) {
            currentPage = localStorageFisrtPageState / localStorageRowPerPage + 1;
        }
        let currentItemsPerPage = itemsPerPage;
        if (!currentItemsPerPage) {
            currentItemsPerPage = localStorageRowPerPage;
        }

        populateDatatable(supplierUuidFromUrl, currentItemsPerPage as number, currentPage, criteria.value, sort);
    }
}

const onSort = (e) => {
    if (supplierUuidFromUrl.length) {
        if (e.sortField) {
            rows.value = e.rows;
            setLocalStorageRowsPerPage(rows.value);
            const sort = {
                fieldLabel: "order[" + e.sortField + "]",
                fieldValue: e.sortOrder === 1 ? "asc" : "desc"
            };
            populateDatatable(supplierUuidFromUrl, rows.value, page.value, criteria.value, sort);
        } else {
            populateDatatable(supplierUuidFromUrl, rows.value, page.value, criteria.value, defaultsSort);
        }
    }
};

async function onPaginate(e) {
    rows.value = e.rows;
    page.value = e.page + 1;
    if (supplierUuidFromUrl.length) {
        setLocalStorageRowsPerPage(rows.value);
        populateDataTableWithLocalStorageFilters(supplierUuidFromUrl, rows.value, page.value);
    }
}

function getLocalStorageData() {
    let storageData = localStorage.getItem(localStorageKey);
    
    if (null !== storageData && "undefined" !== storageData) {
        return JSON.parse(storageData);
    }

    return null;
}

function setLocalStorageRowsPerPage(rowPerPage: number) {
    let storageData = localStorage.getItem(localStorageKey);
    if (null !== storageData) {
        let localStorageObject = JSON.parse(storageData);
        localStorageObject.rows = rowPerPage;
        localStorage.setItem(localStorageKey, JSON.stringify(localStorageObject));
    }
}

function getLocalStorageRowsPerPage() {
    let localStorageData = getLocalStorageData();
    if (null !== localStorageData && localStorageData.rows) {
        return localStorageData.rows;
    }

    return defaultRowsPerPage;
}

function getLocalStorageFisrtPageState() {
    let localStorageData = getLocalStorageData();
    if (null !== localStorageData && localStorageData.first) {
        return localStorageData.first;
    }

    return 0;
}

async function deleteBackOfficeSupplierUser(uuid: string) {
    await swal
        .confirm(
            "Êtes-vous sûr de vouloir supprimer ce utilisateur du back office ?",
            "Annuler",
            "Oui, supprimer",
        )
        .then((result) => {
            if (result.isConfirmed) {
                ApiService.delete("back_office_user_suppliers/" + uuid)
                .then(() => {
                    swal.info("Utilisateur supprimé du back office").then(() => {
                      populateDataTableWithLocalStorageFilters(supplierUuidFromUrl);
                    });
                })
                .catch(() => {
                  Swal.error("La suppression a échoué veuillez réessayer.");
                });
            }
        });
}

async function toggleBackOfficeStatusAndLinkedUsers(backOfficeUserSupplier: IBackOfficeSupplierUser, isActive: boolean) {
    await swal
        .confirm(
            isActive ? "Êtes-vous sûr de vouloir activer le back office ? tous les utilisateurs seront activés" : "Êtes-vous sûr de vouloir désactiver le back office ? tous les utilisateurs seront désactivés",
            "Annuler",
            isActive ? "Oui, activer" :  "Oui, désactiver"
        )
        .then((result) => {
            showSpinner();
            let infoSuccessMsg = isActive ? "Les utilisateurs ont été activés" : "Les utilisateurs ont été désactivés"
            if (result.isConfirmed) {
                ApiService.patchWithoutSlug("/back-office-supplier-edit-users/" + backOfficeUserSupplier.uuid, { isActive: isActive })
                .then(() => {
                    swal.info(infoSuccessMsg).then(() => {
                      populateDataTableWithLocalStorageFilters(supplierUuidFromUrl);
                    });
                })
                .catch(() => {
                  Swal.error("une erreur est survenue, veuillez réessayer.");
                });
            }
        });
}

async function toggleUserStatus(
    userUuid: string,
    isActive: string,
    confirmMessage: string,
    successMessage: string,
    cancelButtonText?: string,
    confirmButtonText?: string,           
    confirmButtonClass?: string,
    cancelButtonClass?: string,
) {
    await swal
        .confirm(
            confirmMessage,
            cancelButtonText,
            confirmButtonText,
            confirmButtonClass,
            cancelButtonClass
        )
        .then((result) => {
            if (result.isConfirmed) {
                showSpinner();
                ApiService.patchWithoutSlug("users/edit-status/" + userUuid, { isActive: isActive })
                .then(() => {
                    swal.info(successMessage);
                    hideSpinner();
                })
                .catch(() => {
                  Swal.error("une erreur est survenue, veuillez réessayer.").then((response: object) => {
                      if (response.isConfirmed) {
                          hideSpinner();
                          populateDatatable(supplierUuidFromUrl, rows.value, page.value, criteria.value);
                      }
                  });
                });
            } else {
              populateDatatable(supplierUuidFromUrl, rows.value, page.value, criteria.value);
            }
        });
}

const redirectToEditUserPage = (userUuid: string) => {
    return "ok"
;};

const addNewBackOfficeUser = () => {
    const backOfficeUserCreateFormModalElement:HTMLDialogElement | null = document.querySelector(`#${addBackOfficeUserFormModalid}`);
    if (backOfficeUserCreateFormModalElement) {
        backOfficeUserCreateFormModalElement.showModal();
    }
};

const showSpinner = () => {
    isLoading.value = true;
}

const hideSpinner = () => {
    isLoading.value = false;
}
</script>